import {ClientBonusExpiry} from '../components/ExpirySettings';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { clientsAPI, transactionsAPI, loyaltyAPI, reportsAPI } from '../services/api';
import { Plus, Search, Upload, Eye, X, ArrowUpCircle, ArrowDownCircle, Download } from 'lucide-react';
import Pagination from '../components/Pagination';
import ClientTimeline from '../components/ClientTimeline';
import SegmentFilterPanel from '../components/SegmentFilterPanel';
import toast from 'react-hot-toast';
import {useAuth} from '../hooks/useAuth';
import HelpHint from '../components/HelpHint';
import {CLIENT_COLUMNS,DEFAULT_CLIENT_COLUMNS,ClientColumnPicker,ClientChannels} from '../components/ClientTableTools';
import './Clients.css';

const RFM_COLORS = {
  'Новые': '#06b6d4', 'Потерянные': '#6b7280', 'Засыпающие': '#fb923c',
  'Перспективные': '#84cc16', 'VIP': '#a78bfa', 'Активные': '#10b981',
  'Требуют внимания': '#f59e0b', 'Спящие': '#ef4444', 'Лояльные': '#6366f1',
};
const getRfmColor = (name) => RFM_COLORS[name] || '#6b7280';

const PER_PAGE = 20;

function ImportModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const [progress, setProgress] = useState(null);
  const [status, setStatus] = useState(null);


  useEffect(() => {
    if (!taskId) return;
    const interval = setInterval(async () => {
      try {
        const res = await clientsAPI.importStatus(taskId);
        setProgress(res.data.progress);
        setStatus(res.data.status);
        if (res.data.status === 'success' || res.data.status === 'failed') {
          clearInterval(interval);
          setLoading(false);
          if (res.data.status === 'success') {
            const p = res.data.progress;
            toast.success(`Создано: ${p?.created || 0}, обновлено: ${p?.updated || 0}`);
            onDone();
          } else {
            toast.error(res.data.message || 'Ошибка импорта');
          }
        }
      } catch { clearInterval(interval); setLoading(false); }
    }, 2000);
    return () => clearInterval(interval);
  }, [taskId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error('Выберите файл'); return; }
    setLoading(true);
    setProgress(null);
    setStatus(null);
    try {
      const res = await clientsAPI.importFile(file, updateExisting);
      setTaskId(res.data.task_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка импорта');
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <h2 className="modal-title">Импорт клиентов</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Файл (.xlsx или .csv)</label>
            <input type="file" accept=".xlsx,.csv" style={{ color: 'var(--text-secondary)', fontSize: 13 }}
              onChange={e => setFile(e.target.files[0])} />
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5, margin: '8px 0 12px' }}>
            Колонки: phone, email, имя, фамилия, отчество, пол, день рождения, уровень, баланс, рассылки.
            <br/>Клиенты без телефона загрузятся по email. Дубли определяются по телефону и email.
          </p>
          <label className="form-check">
            <input type="checkbox" checked={updateExisting} onChange={e => setUpdateExisting(e.target.checked)} />
            Обновлять существующих
          </label>
          {loading && (
            <div style={{ padding: 14, borderRadius: 'var(--radius-sm)', background: 'var(--bg-secondary)', border: '1px solid var(--border)', margin: '16px 0', fontSize: 13 }}>
              <div style={{ marginBottom: 8 }}>
                {status === 'running' ? '⏳ Импорт выполняется...' : '⏳ Запуск...'}
              </div>
              {progress && (
                <>
                  <div>Создано: <strong style={{ color: 'var(--success)' }}>{progress.created || 0}</strong></div>
                  <div>Обновлено: <strong>{progress.updated || 0}</strong></div>
                  <div>Пропущено: <strong>{progress.skipped || 0}</strong></div>
                  {progress.errors?.length > 0 && (
                    <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 6 }}>
                      Ошибки: {progress.errors.length}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          {!loading && progress && status === 'success' && (
            <div style={{ padding: 14, borderRadius: 'var(--radius-sm)', background: 'var(--bg-secondary)', border: '1px solid var(--border)', margin: '16px 0', fontSize: 13 }}>
              <div style={{ color: 'var(--success)', fontWeight: 600, marginBottom: 6 }}>Импорт завершён</div>
              <div>Создано: <strong>{progress.created || 0}</strong></div>
              <div>Обновлено: <strong>{progress.updated || 0}</strong></div>
              <div>Пропущено: <strong>{progress.skipped || 0}</strong></div>
              {progress.errors?.length > 0 && progress.errors.slice(0, 10).map((e, i) => (
                <div key={i} style={{ fontSize: 11, color: 'var(--danger)' }}>Строка {e.row}: {e.error}</div>
              ))}
            </div>
          )}
          {!loading && status === 'failed' && (
            <div style={{ padding: 14, borderRadius: 'var(--radius-sm)', background: 'var(--bg-secondary)', border: '1px solid var(--danger)', margin: '16px 0', fontSize: 13, color: 'var(--danger)' }}>
              Ошибка импорта
            </div>
          )}
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Закрыть</button>
            <button type="submit" className="btn btn-primary" disabled={loading || !file}>
              {loading ? 'Импорт...' : 'Импортировать'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


function CreateClientModal({ tiers, onClose, onCreated }) {
  const [form, setForm] = useState({
    phone: '', name: '', email: '', gender: '', birthdate: '', consent_sms: false, consent_email: false, loyalty_tier: '', balance: 0,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = { ...form, loyalty_tier: form.loyalty_tier || null, balance: parseFloat(form.balance) || 0, gender: form.gender || null, birthdate: form.birthdate || null };
      await clientsAPI.create(data);
      toast.success('Клиент создан');
      onCreated(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h2 className="modal-title">Новый клиент</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Телефон *</label>
            <input className="form-input" required placeholder="+79001234567"
              value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Имя</label>
            <input className="form-input" placeholder="Иван Петров"
              value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" placeholder="ivan@example.com"
              value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Пол</label>
              <select className="form-input" value={form.gender} onChange={e => setForm({ ...form, gender: e.target.value })}>
                <option value="">Не указан</option>
                <option value="male">Мужской</option>
                <option value="female">Женский</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Дата рождения</label>
              <input className="form-input" type="date" value={form.birthdate} onChange={e => setForm({ ...form, birthdate: e.target.value })} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Уровень лояльности</label>
              <select className="form-input" value={form.loyalty_tier}
                onChange={e => setForm({ ...form, loyalty_tier: e.target.value })}>
                <option value="">По умолчанию</option>
                {tiers.map(t => <option key={t.name} value={t.name}>{t.display_name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Начальный баланс</label>
              <input className="form-input" type="number" min="0" step="0.01"
                value={form.balance} onChange={e => setForm({ ...form, balance: e.target.value })} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-check">
              <input type="checkbox" checked={form.consent_sms} onChange={e => setForm({ ...form, consent_sms: e.target.checked })} />
              Согласие на SMS
            </label>
          </div>
          <div className="form-group">
            <label className="form-check">
              <input type="checkbox" checked={form.consent_email} onChange={e => setForm({ ...form, consent_email: e.target.checked })} />
              Согласие на Email
            </label>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Отмена</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? '...' : 'Создать'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}


function ClientCard({ client, tiers, clientSegments = {}, onClose, onUpdated }) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: client.name || '', email: client.email || '',
    gender: client.gender || '', birthdate: client.birthdate || '',
    consent_sms: client.consent_sms, consent_email: client.consent_email,
    loyalty_tier: client.loyalty_tier,
  });
  const [bonusForm, setBonusForm] = useState({ amount: '', comment: '', expires_in_days: '' });
  const [saving, setSaving] = useState(false);
  const [timelineRevision, setTimelineRevision] = useState(0);

  const handleSave = async () => {
    setSaving(true);
    try {
      await clientsAPI.update(client.id, form);
      toast.success('Клиент обновлён');
      setEditing(false);
      onUpdated();
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
    finally { setSaving(false); }
  };

  const handleBonus = async (type) => {
    const amount = parseFloat(bonusForm.amount);
    if (!amount || amount <= 0) { toast.error('Укажите сумму'); return; }
    if (!bonusForm.comment) { toast.error('Укажите комментарий'); return; }
    try {
      const data = { phone: client.phone, amount, comment: bonusForm.comment };
      if (type === 'accrue' && bonusForm.expires_in_days) {
        data.expires_in_days = parseInt(bonusForm.expires_in_days);
      }
      if (type === 'accrue') await transactionsAPI.accrue(data);
      else await transactionsAPI.deduct(data);
      toast.success(type === 'accrue' ? `+${amount} баллов` : `−${amount} баллов`);
      setBonusForm({ amount: '', comment: '', expires_in_days: '' });
      onUpdated();
      setTimelineRevision(value => value + 1);
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 620, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 className="modal-title" style={{ margin: 0 }}>Карточка клиента</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}><X size={20} /></button>
        </div>


        <div className="card-grid" style={{ marginBottom: 20 }}>
          <div className="stat-card">
            <div className="stat-card-label">Баланс</div>
            <div className="stat-card-value" style={{ fontSize: 22 }}>{client.balance.toLocaleString('ru')}</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-label">Заморожено</div>
            <div className="stat-card-value" style={{ fontSize: 22 }}>{client.held_balance.toLocaleString('ru')}</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-label">Доступно</div>
            <div className="stat-card-value" style={{ fontSize: 22 }}>{(client.balance - client.held_balance).toLocaleString('ru')}</div>
          </div>
        </div>

        <ClientBonusExpiry clientId={client.id} revision={timelineRevision}/>
        {editing ? (
          <div style={{ marginBottom: 20 }}>
            <div className="form-group">
              <label className="form-label">Имя</label>
              <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Пол</label>
                <select className="form-input" value={form.gender} onChange={e => setForm({ ...form, gender: e.target.value })}>
                  <option value="">Не указан</option>
                  <option value="male">Мужской</option>
                  <option value="female">Женский</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Дата рождения</label>
                <input className="form-input" type="date" value={form.birthdate} onChange={e => setForm({ ...form, birthdate: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Уровень</label>
              <select className="form-input" value={form.loyalty_tier} onChange={e => setForm({ ...form, loyalty_tier: e.target.value })}>
                {tiers.map(t => <option key={t.name} value={t.name}>{t.display_name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-check"><input type="checkbox" checked={form.consent_sms} onChange={e => setForm({ ...form, consent_sms: e.target.checked })} /> SMS</label>
            </div>
            <div className="form-group">
              <label className="form-check"><input type="checkbox" checked={form.consent_email} onChange={e => setForm({ ...form, consent_email: e.target.checked })} /> Email</label>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-success btn-sm" onClick={handleSave} disabled={saving}>Сохранить</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setEditing(false)}>Отмена</button>
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: 20, fontSize: 13, lineHeight: 2 }}>
            <div><span style={{ color: 'var(--text-muted)' }}>Телефон:</span> <strong style={{ fontFamily: 'var(--font-mono)' }}>{client.phone}</strong></div>
            <div><span style={{ color: 'var(--text-muted)' }}>Имя:</span> {client.name || '—'}</div>
            <div><span style={{ color: 'var(--text-muted)' }}>Email:</span> {client.email || '—'}</div>
            <div><span style={{ color: 'var(--text-muted)' }}>Пол:</span> {client.gender === 'male' ? 'Мужской' : client.gender === 'female' ? 'Женский' : '—'}</div>
            <div><span style={{ color: 'var(--text-muted)' }}>Дата рождения:</span> {client.birthdate ? new Date(client.birthdate).toLocaleDateString('ru') : '—'}</div>
            <div><span style={{ color: 'var(--text-muted)' }}>Уровень:</span> <span className="badge badge-info">{client.loyalty_tier}</span></div>
            <div><span style={{ color: 'var(--text-muted)' }}>SMS:</span> {client.consent_sms ? '✓' : '—'} &nbsp; <span style={{ color: 'var(--text-muted)' }}>Email:</span> {client.consent_email ? '✓' : '—'}</div>
            {clientSegments[client.id] && (
              <div style={{ marginTop: 8 }}>
                <span style={{ color: 'var(--text-muted)' }}>Сегменты:</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                  {clientSegments[client.id]?.rfm && (
                    <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                      background: getRfmColor(clientSegments[client.id].rfm), color: '#fff' }}>
                      RFM {clientSegments[client.id].rfm}
                    </span>
                  )}
                  {(clientSegments[client.id]?.saved || []).map((s, i) => (
                    <span key={i} style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                      background: s.color || '#6366f1', color: '#fff' }}>
                      {s.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setEditing(true)}>Редактировать</button>
              <button className="btn btn-ghost btn-sm" onClick={() => window.open(`/orders?client_id=${client.id}`, '_blank')}>
                Все заказы
              </button>
              <button className="btn btn-danger btn-sm" onClick={async () => {
                if (!window.confirm(`Удалить клиента ${client.phone}? Все транзакции и заказы будут удалены.`)) return;
                try { await clientsAPI.delete(client.id); toast.success('Клиент удалён'); onClose(); onUpdated(); }
                catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
              }}>Удалить</button>
            </div>
          </div>
        )}


        <div style={{ padding: 16, background: 'var(--bg-secondary)', borderRadius: 'var(--radius)', border: '1px solid var(--border)', marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 12 }}>Операции с бонусами</div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 80 }}>
              <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>Сумма</label>
              <input className="form-input" type="number" min="0.01" step="0.01" placeholder="100"
                value={bonusForm.amount} onChange={e => setBonusForm({ ...bonusForm, amount: e.target.value })} />
            </div>
            <div style={{ flex: 2, minWidth: 140 }}>
              <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>Комментарий</label>
              <input className="form-input" placeholder="Причина"
                value={bonusForm.comment} onChange={e => setBonusForm({ ...bonusForm, comment: e.target.value })} />
            </div>
            <div style={{ width: 100 }}>
              <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>Сгорают (дн)</label>
              <input className="form-input" type="number" min="1" placeholder="∞"
                value={bonusForm.expires_in_days} onChange={e => setBonusForm({ ...bonusForm, expires_in_days: e.target.value })} />
            </div>
            <button className="btn btn-success btn-sm" onClick={() => handleBonus('accrue')} title="Начислить"><ArrowUpCircle size={14} /> +</button>
            <button className="btn btn-danger btn-sm" onClick={() => handleBonus('deduct')} title="Списать"><ArrowDownCircle size={14} /> −</button>
          </div>
        </div>


        <ClientTimeline key={client.id} clientId={client.id} revision={timelineRevision} />
      </div>
    </div>
  );
}

export default function ClientsPage() {
  const {user}=useAuth(); const navigate=useNavigate(); const owner=user?.role==='owner';
  const preferenceKey='segmira:client-columns:v1:'+user?.id+':'+user?.account_id;
  const [columns,setColumns]=useState(DEFAULT_CLIENT_COLUMNS),[details,setDetails]=useState({}),[detailsError,setDetailsError]=useState(false),[detailsRetry,setDetailsRetry]=useState(0),[exporting,setExporting]=useState(false),[exportFormat,setExportFormat]=useState('xlsx'),[exportQueued,setExportQueued]=useState(false);
  useEffect(()=>{try{const raw=JSON.parse(localStorage.getItem(preferenceKey));setColumns(Array.isArray(raw)?[...new Set(['phone','name',...raw.filter(x=>CLIENT_COLUMNS.some(c=>c.id===x))])]:DEFAULT_CLIENT_COLUMNS);}catch{setColumns(DEFAULT_CLIENT_COLUMNS);}},[preferenceKey]);
  const changeColumns=value=>{setColumns(value);try{localStorage.setItem(preferenceKey,JSON.stringify(value));}catch{}};

  const [searchParams] = useSearchParams();
  const clientIdFromUrl = searchParams.get('client_id');
  const dashboardFilters=Object.fromEntries(['purchased_from','purchased_to','bonus_expiring_days','rfm_segment','saved_segment','date_from','date_to'].filter(k=>searchParams.get(k)).map(k=>[k,searchParams.get(k)]));
  const hasDashboardFilters=Object.keys(dashboardFilters).length>0;

  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const requestId = useRef(0);
  const [activeQuery, setActiveQuery] = useState({ search: '', sf: {} });
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [search, setSearch] = useState('');
  const [tiers, setTiers] = useState([]);
  const [clientSegments, setClientSegments] = useState({});
  const [filters, setFilters] = useState({
    segments: [], exclude_segments: [], loyalty_tiers: [], exclude_tiers: [],
    place_ids: [], exclude_place_ids: [],
    min_balance: '', max_balance: '', min_total_spent: '', max_total_spent: '',
    min_orders: '', max_orders: '', gender: '', has_email: false, has_birthday_in_days: '',
  });

  const buildSF = () => {
    const sf = {};
    if (filters.rfm_history) sf.rfm_history = filters.rfm_history;
    if (filters.segments?.length) sf.segments = filters.segments;
    if (filters.exclude_segments?.length) sf.exclude_segments = filters.exclude_segments;
    if (filters.loyalty_tiers?.length) sf.loyalty_tiers = filters.loyalty_tiers;
    if (filters.exclude_tiers?.length) sf.exclude_tiers = filters.exclude_tiers;
    if (filters.place_ids?.length) sf.place_ids = filters.place_ids;
    if (filters.exclude_place_ids?.length) sf.exclude_place_ids = filters.exclude_place_ids;
    ['min_balance','max_balance','min_total_spent','max_total_spent'].forEach(k => {
      if (filters[k] !== '' && filters[k] !== undefined) sf[k] = parseFloat(filters[k]);
    });
    ['min_orders','max_orders','has_birthday_in_days'].forEach(k => {
      if (filters[k] !== '' && filters[k] !== undefined) sf[k] = parseInt(filters[k]);
    });
    if (filters.gender) sf.gender = filters.gender;
    if (filters.has_email) sf.has_email = true;
    if (filters.consent_sms) sf.consent_sms = true;
    if (filters.consent_email) sf.consent_email = true;
    return sf;
  };

  const load = (p = page) => {
    const id = ++requestId.current;
    setLoading(true);
    setLoadError('');
    const skip = (p - 1) * PER_PAGE;
    const { search: q, sf } = activeQuery;
    const request = !hasDashboardFilters&&Object.keys(sf).length
      ? clientsAPI.filtered({ skip, limit: PER_PAGE, search: q || null, segment_filter: sf })
      : (hasDashboardFilters?clientsAPI.list(skip, PER_PAGE, q || null,dashboardFilters):clientsAPI.list(skip, PER_PAGE, q || null));
    return request.then(res => {
      if (id !== requestId.current) return;
      setClients(res.data.items); setTotal(res.data.total);
    }).catch(() => {
      if (id === requestId.current) setLoadError('Не удалось загрузить клиентов. Повторите попытку.');
    }).finally(() => {
      if (id === requestId.current) setLoading(false);
    });
  };

  const queryKey = JSON.stringify({ search, sf: buildSF() });
  useEffect(() => {
    if (queryKey === JSON.stringify(activeQuery)) return;
    const timer = setTimeout(() => {
      setPage(1);
      setActiveQuery(JSON.parse(queryKey));
    }, 400);
    return () => clearTimeout(timer);
  }, [queryKey]);

  useEffect(() => {
    load(page);
    return () => { ++requestId.current; };
  }, [page, activeQuery]);

  useEffect(() => {
    loyaltyAPI.tiers().then(res => setTiers(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (clientIdFromUrl) {
      clientsAPI.get(clientIdFromUrl).then(r => setSelectedClient(r.data)).catch(() => {});
    }
  }, [clientIdFromUrl]);


  useEffect(() => {
    let current = true;
    if (clients.length > 0) {
      const ids = clients.map(c => c.id);
      clientsAPI.segmentsBatch(ids).then(r => { if (current) setClientSegments(r.data || {}); }).catch(() => {});
    } else {
      setClientSegments({});
    }
    return () => { current = false; };
  }, [JSON.stringify(clients.map(c => c.id))]);


  useEffect(()=>{
    let current=true;setDetails({});setDetailsError(false);
    if(clients.length)clientsAPI.tableDetails(clients.map(c=>c.id)).then(r=>{if(current)setDetails(r.data);}).catch(()=>{if(current)setDetailsError(true);});
    return()=>{current=false;};
  },[clients,detailsRetry]);
  const visibleColumns=CLIENT_COLUMNS.filter(c=>columns.includes(c.id));
  const exportSelection=async()=>{
    if(!owner||loading||loadError||exporting)return;setExporting(true);
    try{const fields=visibleColumns.flatMap(c=>c.id==='channels'?['consent_sms','consent_email','telegram_linked','max_linked','push_linked']:[c.id]);
      await reportsAPI.queue({request_id:crypto.randomUUID(),name:'Клиенты — текущая выборка',definition:{kind:'clients',format:exportFormat,columns:[...new Set(['id',...fields])],filters:{client_selection:activeQuery.search||Object.keys(activeQuery.sf).length?{search:activeQuery.search||null,segment_filter:activeQuery.sf}:{}}}});
      setExportQueued(true);toast.success('Выгрузка поставлена в очередь');
    }catch(e){const message=e.response?.data?.detail;toast.error(typeof message==='string'?message:'Не удалось подготовить выгрузку');}finally{setExporting(false);}
  };
  const filtered = clients;

  const totalPages = Math.ceil((total || 0) / PER_PAGE);

  const tierDisplay = (name) => {
    const t = tiers.find(t => t.name === name);
    return t
      ? <span className="badge" style={{ background: t.color + '20', color: t.color }}>{t.display_name}</span>
      : <span className="badge badge-info">{name}</span>;
  };

  return (
    <div className="animate-in">
      <div className="page-header">
        <h1 className="page-title">Клиенты</h1>
        <p className="page-subtitle">{total === null ? (loadError ? 'Количество недоступно' : 'Загружаем клиентов…') : `Всего: ${total.toLocaleString('ru-RU')}`}</p>
      </div>

      <div className="client-table-toolbar">
        <div className="client-table-search"><Search size={16}/><input className="form-input" aria-label="Поиск клиентов" maxLength={255} placeholder="Телефон, имя или Email" value={search} onChange={e=>setSearch(e.target.value)}/></div>
        {!hasDashboardFilters&&<div className="client-table-filters"><SegmentFilterPanel filters={filters} onChange={setFilters}/></div>}
        <div className="client-table-actions"><ClientColumnPicker value={columns} onChange={changeColumns}/>
          {owner&&!hasDashboardFilters&&<><select className="form-input client-export-format" aria-label="Формат выгрузки клиентов" value={exportFormat} onChange={e=>setExportFormat(e.target.value)}><option value="xlsx">XLSX</option><option value="csv">CSV</option></select><button type="button" className="btn btn-ghost btn-sm" disabled={loading||!!loadError||exporting||queryKey!==JSON.stringify(activeQuery)||!total} onClick={exportSelection}><Download size={15}/>{exporting?'Готовим…':'Выгрузить'}</button></>}
          {['owner','operator'].includes(user?.role)&&<button className="btn btn-primary btn-sm" onClick={()=>setShowCreate(true)}><Plus size={15}/>Добавить</button>}
          {owner&&<button className="btn btn-ghost btn-sm" onClick={()=>setShowImport(true)}><Upload size={15}/>Импорт</button>}
        </div>
      </div>
      {exportQueued&&<div className="client-table-notice" role="status">Файл готовится в фоне по текущему поиску и фильтрам. <button className="btn btn-ghost btn-sm" onClick={()=>navigate('/reports')}>Открыть выгрузки</button></div>}
      {detailsError&&<div className="client-table-notice" role="alert">Не удалось загрузить каналы и покупки. <button className="btn btn-ghost btn-sm" onClick={()=>setDetailsRetry(n=>n+1)}>Повторить</button></div>}

      {hasDashboardFilters&&<p className="dashboard-note">Выборка с дашборда: {dashboardFilters.bonus_expiring_days?`Бонусы сгорят за ${dashboardFilters.bonus_expiring_days} дней`:dashboardFilters.rfm_segment||dashboardFilters.saved_segment||(dashboardFilters.purchased_from?`Покупатели ${dashboardFilters.purchased_from} — ${dashboardFilters.purchased_to}`:null)||`Новые карточки ${dashboardFilters.date_from} — ${dashboardFilters.date_to}`} · <a href="/clients">Сбросить</a>. Выгрузка этой выборки пока недоступна.</p>}
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              {visibleColumns.map(c=><th key={c.id}>{c.label}<HelpHint label={c.label} text={c.help}/></th>)}<th><span className="sr-only">Действия</span></th>
            </tr>
          </thead>
          <tbody aria-busy={loading}>
            {loading && <tr><td colSpan={visibleColumns.length+1} role="status" style={{ padding: 16, color: 'var(--text-muted)' }}>{clients.length ? 'Обновляем список…' : 'Загружаем клиентов…'}</td></tr>}
            {loadError && <tr><td colSpan={visibleColumns.length+1} role="alert" style={{ padding: 16 }}>{loadError} <button className="btn btn-sm" onClick={() => load(page)}>Повторить</button></td></tr>}
            {filtered.map(c => (
              <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedClient(c)}>
                {visibleColumns.map(col=>{
                  const d=details[c.id];let value;
                  const date=value=>value?new Date(value.endsWith?.('Z')?value:value+'Z'):null;
                  if(col.id==='created_at'){const t=date(c.created_at);value=t?<><span>{t.toLocaleDateString('ru-RU')}</span><small className="client-table-time">{t.toLocaleTimeString('ru-RU',{hour:'2-digit',minute:'2-digit'})}</small></>:'—';}
                  else if(col.id==='channels')value=<ClientChannels client={c} details={d}/>;
                  else if(col.id==='loyalty_tier')value=tierDisplay(c.loyalty_tier);
                  else if(col.id==='gender')value=c.gender==='male'?'М':c.gender==='female'?'Ж':'—';
                  else if(col.id==='birthdate')value=c.birthdate?c.birthdate.split('-').reverse().join('.'):'—';
                  else if(col.id==='segments')value=(                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {clientSegments[c.id]?.rfm && (
                      <span style={{ display: 'inline-block', padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                        background: clientSegments[c.id].rfm_color || getRfmColor(clientSegments[c.id].rfm), color: '#fff' }}>
                        RFM {clientSegments[c.id].rfm}
                      </span>
                    )}
                    {(clientSegments[c.id]?.saved || []).map((s, i) => (
                      <span key={i} style={{ display: 'inline-block', padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                        background: s.color || '#6366f1', color: '#fff' }}>
                        {s.name}
                      </span>
                    ))}
                  </div>);
                  else if(col.id==='last_purchase')value=d?(d.last_purchase?date(d.last_purchase).toLocaleDateString('ru-RU'):'—'):'…';
                  else if(['order_count','total_spent','available_balance'].includes(col.id))value=d?(d[col.id]??0).toLocaleString('ru-RU')+(col.id==='total_spent'?' ₽':''):'…';
                  else if(['balance','held_balance'].includes(col.id))value=(c[col.id]??0).toLocaleString('ru-RU');
                  else value=c[col.id]||'—';
                  return <td key={col.id} className={'client-cell-'+col.id}>{value}</td>;
                })}
                <td>
                  <button className="btn btn-ghost btn-sm" aria-label={'Открыть карточку: '+(c.name||c.phone||'клиент')} onClick={e => { e.stopPropagation(); setSelectedClient(c); }}>
                    <Eye size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {!loading && !loadError && total !== null && filtered.length === 0 && (
              <tr><td colSpan={visibleColumns.length+1} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                {total === 0 ? 'Клиентов пока нет' : 'Ничего не найдено'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />

      {showCreate && <CreateClientModal tiers={tiers} onClose={() => setShowCreate(false)} onCreated={() => load(page)} />}
      {showImport && <ImportModal onClose={() => setShowImport(false)} onDone={() => load(page)} />}
      {selectedClient && <ClientCard client={selectedClient} tiers={tiers} clientSegments={clientSegments} onClose={() => setSelectedClient(null)} onUpdated={() => { load(page); clientsAPI.get(selectedClient.id).then(r => setSelectedClient(r.data)).catch(() => setSelectedClient(null)); }} />}
    </div>
  );
}